import { GOOGLE_MAPS_KEY } from "@/src/const";

type LatLng = {
  lat: number;
  lng: number;
};

type GeoLocateOutput =
  | {
      success: false;
    }
  | {
      success: true;
      location: LatLng;
    };

/**
 * Get the "best guess" geolocation of the device from google
 *
 * @documentation https://developers.google.com/maps/documentation/geolocation/overview
 * @returns
 */
async function geoLocate(): Promise<GeoLocateOutput> {
  const url: string = `https://www.googleapis.com/geolocation/v1/geolocate?key=${GOOGLE_MAPS_KEY}`;
  const response = await fetch(url, { method: "POST" });
  if (response.ok) {
    const parsedResponse = await response.json();
    return { success: true, location: parsedResponse.location };
  }
  return { success: false };
}

type ReverseGeoCodingOutput =
  | {
      success: false;
    }
  | {
      success: true;
      location: any;
    };

/**
 * Get the address for a latitude / longitude from google
 *
 * @documentation https://developers.google.com/maps/documentation/geocoding/requests-reverse-geocoding
 * @param latitude
 * @param longitude
 * @returns
 */
async function getReverseGeoCoding(input: LatLng): Promise<ReverseGeoCodingOutput> {
  const { lat, lng } = input;
  const url: string = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${GOOGLE_MAPS_KEY}`;
  const response = await fetch(url, { method: "GET" });
  if (response.ok) {
    const location = await response.json();
    if (location.status !== "OK") {
      console.error(`Google Maps Reverse Geocoding Error: ${JSON.stringify(location)}`);
      return { success: false };
    }
    return { success: true, location };
  }
  return { success: false };
}

export interface AddressComponents {
  address1: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  placeId: string;
}

/**
 * Take an address result from google and parse into a usable address
 *
 * @param addressResult
 * @returns
 */
function parseAddressComponents(addressResult: any): AddressComponents | undefined {
  if (!addressResult || !Array.isArray(addressResult.address_components)) {
    return;
  }

  const obj: AddressComponents = {
    address1: "",
    city: "",
    state: "",
    country: "",
    postalCode: "",
    placeId: addressResult.place_id,
  };

  let streetNumber = "";
  let streetName = "";
  for (const component of addressResult.address_components) {
    if (Array.isArray(component.types)) {
      if (component.types.includes("street_number")) {
        streetNumber = component.short_name;
      }
      if (component.types.includes("route")) {
        streetName = component.short_name;
      } else if (component.types.includes("locality")) {
        obj.city = component.short_name;
      } else if (component.types.includes("administrative_area_level_1")) {
        obj.state = component.short_name;
      } else if (component.types.includes("postal_code")) {
        obj.postalCode = component.short_name;
      } else if (component.types.includes("country")) {
        obj.country = component.short_name;
      }
    }
  }
  obj.address1 = `${streetNumber} ${streetName}`;

  return obj;
}

export default {
  parseAddressComponents,
  geoLocate,
  getReverseGeoCoding,
};
